import { useState } from 'react';
import { GridRowModes, GridRowEditStopReasons } from '@mui/x-data-grid';

/**
 * Shared hook for DataGridPro inline row editing state management.
 * Replaces the duplicated rowModesModel state and handler functions
 * (handleEditClick, handleSaveClick, handleCancelClick, etc.)
 * that were copy-pasted across all editable table components.
 *
 * @param {Object}   options
 * @param {Array}    options.rows     - Current rows state array
 * @param {Function} options.setRows  - State setter for rows
 * @param {Function} [options.getRowId] - Row ID accessor (defaults to row.id).
 *                                        Override for tables with non-standard IDs (e.g. ShipTable uses row.imo).
 *
 * Usage:
 *   const { rowModesModel, handleEditClick, ... } = useRowEditing({ rows, setRows });
 */
const useRowEditing = ({ rows, setRows, getRowId = (row) => row.id }) => {
  const [rowModesModel, setRowModesModel] = useState({});

  // Prevent row from exiting edit mode when clicking outside the row
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  // Switch a row into edit mode
  const handleEditClick = (id) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  };

  // Switch a row back to view mode (triggers processRowUpdate)
  const handleSaveClick = (id) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View },
    }));
  };

  // Revert changes and exit edit mode; removes unsaved new rows (isNew flag)
  const handleCancelClick = (id) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
    const editedRow = rows.find((row) => getRowId(row) === id);
    if (editedRow?.isNew) {
      setRows((prev) => prev.filter((row) => getRowId(row) !== id));
    }
  };

  // Sync DataGridPro's internal row modes model with our state
  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  // Programmatically enter edit mode for a row, focusing a specific field
  // (used after adding a new row to immediately open it for editing)
  const startEditingRow = (id, fieldToFocus) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit, fieldToFocus },
    }));
  };

  return {
    rowModesModel,
    setRowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
  };
};

export default useRowEditing;
